import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Download } from 'lucide-react'
import { PVC } from './PVC/PVC'
import { HDPE } from './HDPE/HDPE'
import { Button } from '../../components/Button/Button'
import { useQuoteModal } from '../../context/QuoteContext'

function Products() {
	const { openQuoteModal } = useQuoteModal()
	const location = useLocation()

	// HASH SCROLL HANDLER (Fixes incoming links like /products#pvc)
	useEffect(() => {
		if (location.hash) {
			const targetId = location.hash.replace('#', '')
			const element = document.getElementById(targetId)
			if (element) {
				setTimeout(() => {
					element.scrollIntoView({ behavior: 'smooth' })
				}, 100)
			}
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' })
		}
	}, [location])

	return (
		<main className='bg-brand-light min-h-screen'>
			{/* 1. SIMPLE HERO HEADER (MATCHED WITH HOME & ABOUT) */}
			<section className='bg-brand-dark text-white py-12 lg:py-16 border-b border-slate-800 border-t-2 border-t-brand-red'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<h1 className='font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-4xl'>
						Product{' '}
						<span className='text-brand-blue'>Catalog & Specifications</span>
					</h1>
					<p className='text-slate-300 text-base sm:text-lg max-w-3xl mt-3 leading-relaxed'>
						Review product dimensions, specifications, and sizing details across
						our core PVC and HDPE product lines.
					</p>

					{/* Quick Jump Anchor Pills */}
					<div className='flex flex-wrap items-center gap-3 mt-6'>
						<span className='text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1'>
							Quick Jump:
						</span>
						<a
							href='#pvc'
							className='text-xs font-semibold px-4 py-2 bg-slate-800 hover:bg-brand-blue text-slate-200 hover:text-white rounded-lg transition-all border border-slate-700/80 shadow-sm'>
							PVC Range →
						</a>
						<a
							href='#hdpe'
							className='text-xs font-semibold px-4 py-2 bg-slate-800 hover:bg-brand-blue text-slate-200 hover:text-white rounded-lg transition-all border border-slate-700/80 shadow-sm'>
							HDPE Range →
						</a>
					</div>
				</div>
			</section>

			{/* 2. MAIN SPECIFICATION TABLES CONTAINER */}
			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 space-y-16'>
				{/* PVC SECTION HEADER & TABLE */}
				<div id='pvc' className='scroll-mt-24 space-y-6'>
					<div className='flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-brand-border pb-6'>
						<div>
							<h2 className='font-heading text-2xl sm:text-3xl font-bold text-brand-dark'>
								PVC Piping & Fittings
							</h2>
							<p className='text-slate-600 text-sm mt-1 max-w-2xl'>
								Quality PVC conduit and fitting solutions engineered for
								construction, agriculture, and industrial applications.
							</p>
						</div>

						<a
							href='/docs/pvc-specifications.pdf'
							target='_blank'
							rel='noopener noreferrer'
							download
							className='inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-brand-dark bg-white border border-brand-border hover:border-brand-blue hover:text-brand-blue hover:bg-slate-50 rounded-xl transition-all shrink-0 shadow-sm'>
							<Download className='w-4 h-4 text-brand-blue' />
							Download PVC Data Sheet (PDF)
						</a>
					</div>

					<PVC />
				</div>

				{/* HDPE SECTION HEADER & TABLE */}
				<div id='hdpe' className='scroll-mt-24 space-y-6'>
					<div className='flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-brand-border pb-6'>
						<div>
							<h2 className='font-heading text-2xl sm:text-3xl font-bold text-brand-dark'>
								HDPE Pressure Piping
							</h2>
							<p className='text-slate-600 text-sm mt-1 max-w-2xl'>
								Durable high-density polyethylene pipes designed for mining,
								municipal water reticulation, and fluid conveyance.
							</p>
						</div>

						<a
							href='/docs/hdpe-specifications.pdf'
							target='_blank'
							rel='noopener noreferrer'
							download
							className='inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-brand-dark bg-white border border-brand-border hover:border-brand-blue hover:text-brand-blue hover:bg-slate-50 rounded-xl transition-all shrink-0 shadow-sm'>
							<Download className='w-4 h-4 text-brand-blue' />
							Download HDPE Data Sheet (PDF)
						</a>
					</div>

					<HDPE />
				</div>
			</div>

			{/* 3. BOTTOM PROCUREMENT CTA BANNER */}
			<section className='bg-brand-dark text-white py-12 border-t-2 border-t-brand-red'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left'>
					<div>
						<h3 className='font-heading text-xl sm:text-2xl font-bold'>
							Need Custom Sizing or Bulk Project Quotes?
						</h3>
						<p className='text-slate-300 text-sm mt-1 max-w-xl'>
							Get in touch with our team for specific sizing, product
							availability, and direct project pricing.
						</p>
					</div>
					<div className='shrink-0'>
						<Button size='md' variant='primary' onClick={openQuoteModal}>
							Request a Quote
						</Button>
					</div>
				</div>
			</section>
		</main>
	)
}

export default Products
